const SIGNUP_DRAFT_KEY = "signup-draft";

export type SignupIntent = "publish" | "find_projects";
export type SignupPublisherKind = "company" | "solo_freelancer";
export type SignupStep = "intent" | "publisher-kind" | "signup" | "email-sent";

export interface SignupDraft {
  step: SignupStep;
  signupIntent: SignupIntent | null;
  publisherKind: SignupPublisherKind | null;
  accountType?: "consumer" | "provider" | null;
  email: string | null;
  full_name: string;
  location: string;
  send_email: boolean;
  accept_terms: boolean;
}

const DEFAULT_DRAFT: SignupDraft = {
  step: "intent",
  signupIntent: null,
  publisherKind: null,
  email: null,
  full_name: "",
  location: "IT",
  send_email: false,
  accept_terms: false,
};

/**
 * Reads the persisted signup draft from localStorage (sync).
 * Returns null if missing, invalid, or step is email-sent (signup completed).
 */
export function getSignupDraft(): SignupDraft | null {
  const draft = getSignupDraftForRedirect();
  if (draft?.step === "email-sent") return null;
  return draft;
}

export function getSignupDraftForRedirect(): SignupDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SIGNUP_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as SignupDraft;
    return {
      ...DEFAULT_DRAFT,
      ...draft,
      signupIntent:
        draft.signupIntent ??
        ("accountType" in draft
          ? draft.accountType === "provider"
            ? "publish"
            : draft.accountType === "consumer"
              ? "find_projects"
              : null
          : null),
      publisherKind:
        draft.publisherKind ??
        ("accountType" in draft && draft.accountType === "provider"
          ? "company"
          : null),
    };
  } catch {
    return null;
  }
}

/**
 * Persists the signup draft to localStorage.
 * Call when the user is on account-type or signup step and form/store change.
 */
export function setSignupDraft(draft: SignupDraft): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SIGNUP_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Clears the persisted signup draft (e.g. after successful signup).
 */
export function clearSignupDraft(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(SIGNUP_DRAFT_KEY);
  } catch {
    // ignore
  }
}

/**
 * The signup intent as the BE stores it (`profiles.signup_intent`).
 *
 * The client keeps the choice in two fields because the wizard asks it in two
 * steps; the column is one value because that is the single question it answers:
 * *what did this person say they came here to do*.
 */
export type SignupIntentWireValue =
  | "publish_company"
  | "publish_solo_freelancer"
  | "find_projects";

/**
 * Flattens the two-step choice into the wire value, or `undefined` when the
 * wizard has not asked enough yet.
 *
 * This is what makes the choice survive the signup. Until the column existed,
 * the intent lived only in the local draft, and `VerifyRoute` cleared that draft
 * the moment it routed to the company onboarding — so a user who closed the app
 * there came back indistinguishable from a freelancer who had stalled, and the
 * session bootstrap sent them to the freelance onboarding.
 *
 * `undefined` rather than a fallback on purpose: guessing "find_projects" for an
 * incomplete answer would write the wrong intent, which is worse than none.
 */
export const toSignupIntentWireValue = (
  signupIntent: SignupIntent | null,
  publisherKind: SignupPublisherKind | null,
): SignupIntentWireValue | undefined => {
  if (signupIntent === "find_projects") return "find_projects";
  if (signupIntent !== "publish") return undefined;
  if (publisherKind === "company") return "publish_company";
  if (publisherKind === "solo_freelancer") return "publish_solo_freelancer";
  return undefined;
};

export { DEFAULT_DRAFT };
